import { Label } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules';
import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { DownloadOutlined } from '@ant-design/icons';
import { Col, DatePicker, Row, message } from 'antd';
import fileDownload from 'js-file-download';
import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { MODULE_PAYROLL_ORDER } from '../../../constants/modules';
import { downloadPayrollValueBandsChart } from '../../../services/repository/dashboardRepository';
import { createDateDisabler } from '../../../services/util/date';
import BwgButton from '../../atoms/Button';
import SelectFolha from '../../atoms/SelectFolha';
import PayrollValueCard from './PayrollValueCard';
import ValueBandsChart from './ValueBandsChart';
import ValueBandsDrillDownModal from './ValueBandsDrillDownModal';

const { MonthPicker } = DatePicker;

const COMPETENCE_FORMAT = 'MM/YYYY';
const FILE_NAME_COMPETENCE_FORMAT = 'MM-YYYY';
// RN3: por padrão o gráfico mostra os últimos 6 meses, contando o mês atual
const DEFAULT_COMPETENCE_RANGE_MONTHS = 6;
// RN5: intervalo entre competência inicial e final não pode ultrapassar 24 meses
const MAX_COMPETENCE_RANGE_MONTHS = 24;
// Definido com o PO: 09/2025 tem dados, mas incompletos (abaixo do volume de um mês cheio) e distorceriam a análise
const FIRST_AVAILABLE_COMPETENCE = moment('2025-10-01');

export function resolveMinStartCompetence(competenciaFinal) {
  const maxRangeStart = moment(competenciaFinal).subtract(MAX_COMPETENCE_RANGE_MONTHS - 1, 'months');
  return moment.max(maxRangeStart, FIRST_AVAILABLE_COMPETENCE);
}

const CompetencePickers = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

function Dashboard() {
  const [selectedFolhas, setSelectedFolhas] = useState([]);
  const [competenciaInicial, setCompetenciaInicial] = useState(
    moment()
      .subtract(DEFAULT_COMPETENCE_RANGE_MONTHS - 1, 'months')
      .startOf('month')
  );
  const [competenciaFinal, setCompetenciaFinal] = useState(moment().startOf('month'));
  const [selectedBand, setSelectedBand] = useState(undefined);
  // nenhuma folha selecionada: o backend considera todas as folhas acessíveis
  const folhaIds = selectedFolhas.map((folha) => folha.idFolha);

  const disableCompetenciaInicial = createDateDisabler({
    maxDate: competenciaFinal,
    minDate: resolveMinStartCompetence(competenciaFinal),
    unit: 'month',
  });

  const disableCompetenciaFinal = createDateDisabler({
    minDate: competenciaInicial,
    maxDate: competenciaInicial && moment(competenciaInicial).add(MAX_COMPETENCE_RANGE_MONTHS - 1, 'months'),
    unit: 'month',
  });

  const handleExportClick = async () => {
    const hide = message.loading('Seu download está sendo preparado');
    try {
      const worksheet = await downloadPayrollValueBandsChart({
        folhaIds,
        competenciaInicial: competenciaInicial.format(COMPETENCE_FORMAT),
        competenciaFinal: competenciaFinal.format(COMPETENCE_FORMAT),
      });
      const period = `${competenciaInicial.format(FILE_NAME_COMPETENCE_FORMAT)}-a-${competenciaFinal.format(
        FILE_NAME_COMPETENCE_FORMAT
      )}`;
      fileDownload(worksheet, `faixas-remuneracao-${period}.xlsx`);
    } catch (e) {
      mostrarErro(e);
    } finally {
      hide();
    }
  };

  return (
    <Page
      projeto="Cooperativa"
      titulo="Dashboard"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      <Row
        gutter={[24, 16]}
        justify="space-between"
        align="bottom"
      >
        <Col
          xs={24}
          md={8}
        >
          <SelectFolha
            isMulti
            showLabel
            module={MODULE_PAYROLL_ORDER}
            placeholder="Selecione..."
            folhaSelecionada={selectedFolhas}
            onChange={setSelectedFolhas}
          />
        </Col>

        <Col
          xs={24}
          md={10}
        >
          <Label label="Período">
            <CompetencePickers>
              <MonthPicker
                allowClear={false}
                format={COMPETENCE_FORMAT}
                value={competenciaInicial}
                disabledDate={disableCompetenciaInicial}
                onChange={(data) => data && setCompetenciaInicial(data)}
              />
              <span>até</span>
              <MonthPicker
                allowClear={false}
                format={COMPETENCE_FORMAT}
                value={competenciaFinal}
                disabledDate={disableCompetenciaFinal}
                onChange={(data) => data && setCompetenciaFinal(data)}
              />
            </CompetencePickers>
          </Label>
        </Col>

        <Col
          xs={24}
          md={6}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <BwgButton
            icon={<DownloadOutlined />}
            onClick={handleExportClick}
          >
            Exportar Excel
          </BwgButton>
        </Col>
      </Row>

      <Row
        gutter={[24, 16]}
        style={{ marginTop: 24 }}
      >
        <Col
          xs={24}
          md={12}
        >
          <PayrollValueCard
            folhaIds={folhaIds}
            competenciaInicial={competenciaInicial.format(COMPETENCE_FORMAT)}
            competenciaFinal={competenciaFinal.format(COMPETENCE_FORMAT)}
          />
        </Col>
      </Row>

      <ValueBandsChart
        folhaIds={folhaIds}
        competenciaInicial={competenciaInicial.format(COMPETENCE_FORMAT)}
        competenciaFinal={competenciaFinal.format(COMPETENCE_FORMAT)}
        onSelectBand={setSelectedBand}
      />

      <ValueBandsDrillDownModal
        open={!!selectedBand}
        onClose={() => setSelectedBand(undefined)}
        folhaIds={folhaIds}
        competencia={selectedBand?.competencia}
        faixaId={selectedBand?.faixaId}
        bandLabel={selectedBand?.bandLabel}
      />
    </Page>
  );
}

export default Dashboard;
